import { Engine } from "@babylonjs/core";
import { ROYALE_ROOM_ID } from "@tiny-seas/shared";
import { audio } from "./audio.ts";
import { GameClient } from "./game.ts";
import { JoinError, Net, type GameRoom } from "./net.ts";
import { loadGameAssets } from "./scene/assets.ts";
import { GRAPHICS_PRESETS } from "./scene/graphics.ts";
import { settings } from "./settings.ts";
import "./style.css";
import { Lobby, fatalScreen } from "./ui/lobby.ts";

const ASSETS_FAILED = "Can't load the game's models and textures. Check your connection and try again.";

const canvas = document.getElementById("game");
const ui = document.getElementById("ui");
if (!(canvas instanceof HTMLCanvasElement) || !ui) throw new Error("missing #game canvas or #ui root");

function createEngine(c: HTMLCanvasElement): Engine | null {
  try {
    const probe = c.getContext("webgl2") ?? c.getContext("webgl");
    if (!probe) return null;
    return new Engine(c, true, { preserveDrawingBuffer: false, stencil: true, antialias: true, powerPreference: "high-performance" }, false);
  } catch (e) {
    console.error("[tiny-seas] WebGL init failed", e);
    return null;
  }
}

const engine = createEngine(canvas);
if (!engine) {
  fatalScreen(ui, "Your browser can't show the Tiny Seas", "Tiny Seas needs WebGL. Please use a recent desktop Chrome, Edge or Firefox with hardware acceleration enabled, then reload this page.");
} else {
  const eng = engine;
  // Follow display/browser zoom changes without supersampling Balanced on high-DPI screens.
  const fitPixelRatio = (): void => {
    const scale = 1 / Math.min(window.devicePixelRatio || 1, GRAPHICS_PRESETS[settings.value.graphicsPreset].maxPixelRatio);
    eng.setHardwareScalingLevel(scale);
  };
  fitPixelRatio();
  window.addEventListener("resize", fitPixelRatio);
  const unsubscribeGraphics = settings.subscribe(fitPixelRatio);
  eng.onDisposeObservable.addOnce(() => {
    unsubscribeGraphics();
    window.removeEventListener("resize", fitPixelRatio);
  });
  const net = new Net();
  // Developer network monitor; it meters the game socket, so it must load before one opens.
  // Imported dynamically so normal sessions never download its code or styles.
  const netMonitor = new URLSearchParams(location.search).get("net") === "1" ? (await import("./net-monitor.ts")).mountNetMonitor() : null;
  let lobby: Lobby | null = null;

  const start = (room: GameRoom): void => {
    lobby?.dispose();
    lobby = null;
    canvas.hidden = false;
    // Crew invites always join the same public sea.
    const params = new URLSearchParams(location.search);
    params.delete("room");
    if (room.roomId === ROYALE_ROOM_ID) params.set("mode", "royale");
    else params.delete("mode");
    const crew = room.state.players.get(room.sessionId)?.crew;
    if (crew !== undefined && crew >= 0) params.set("crew", String(crew));
    else params.delete("crew");
    history.replaceState(null, "", `?${params}`);
    const game = new GameClient(eng, room, net, ui, (message) => {
      netMonitor?.detach();
      showLobby(message);
    });
    netMonitor?.attach(game.netProbe());
  };

  const attempt = async (fn: () => Promise<GameRoom>): Promise<void> => {
    audio.unlock();
    try {
      // Usually finished while the player was typing; a failed load is retried here.
      await loadGameAssets().catch((e: unknown) => {
        console.error("[tiny-seas] asset load failed", e);
        throw new JoinError("unreachable", ASSETS_FAILED);
      });
      start(await fn());
    } catch (e) {
      if (!(e instanceof JoinError)) console.error("[tiny-seas] game failed to start", e);
      // The server accepted the join; building the game on this device failed.
      const err = e instanceof JoinError ? e : new JoinError("unreachable", `The game failed to start: ${e instanceof Error ? e.message : String(e)}`);
      lobby?.error(err.message, err.kind === "unreachable");
    }
  };

  const showLobby = (message?: string): void => {
    canvas.hidden = true;
    lobby = new Lobby(
      ui,
      {
        join: (name, crew, mode) => attempt(() => net.join(name, crew, mode)),
      },
      message,
    );
  };

  // Models and maps download while the lobby is up; joining waits for both
  // and retries a failed download, so the prefetch's own failure is ignored here.
  loadGameAssets().catch(() => undefined);
  // Recordings download alongside; sounds use synthesized stand-ins until each is ready.
  void audio.preload();
  // A page reload inside the reconnection window resumes the same seat.
  const resumed = await net.resume();
  if (!resumed) showLobby();
  else if (await loadGameAssets().then(() => true, () => false)) start(resumed);
  else {
    net.forget();
    void resumed.leave(true);
    showLobby(ASSETS_FAILED);
  }
}
